import { DOCUMENT_SUBCATEGORIES, documentCategorySchema } from '@teo/types';
import { z } from 'zod';
import { displayId, documentCategorizationSchema, entityInputSchema, entityOutputSchema, llmChatMessageSchema, } from './common.js';
export const createDocumentSchema = z.object({
    entityInput: entityInputSchema,
    url: z.string().min(1).max(500),
    filename: z.string().min(1).max(255),
    type: z.string().min(1).max(100),
});
export const updateDocumentSchema = z.object({
    documentId: displayId,
    url: z.string().min(1).max(500).optional(),
    filename: z.string().min(1).max(255).optional(),
    type: z.string().min(1).max(100).optional(),
});
export const getDocumentSchema = z.object({
    documentId: displayId,
});
export const deleteDocumentSchema = z.object({
    documentId: displayId,
});
const subcategoryCatalog = Object.entries(DOCUMENT_SUBCATEGORIES)
    .map(([category, { keys }]) => `${category}: ${keys.join(', ')}`)
    .join('. ');
export const updateDocumentCategorySchema = z.object({
    documentId: displayId.describe('A document display id, such as DOCMH2J3K4M5.'),
    category: documentCategorySchema,
    subcategory: z
        .string()
        .min(1)
        .max(255)
        .nullable()
        .describe(`A subcategory of the new category. Null means the document has none. The subcategories of each category are: ${subcategoryCatalog}.`),
});
export const setSecondaryCategorizationSchema = z.object({
    documentId: displayId.describe('A document display id, such as DOCMH2J3K4M5.'),
    subcategory: z
        .string()
        .min(1)
        .max(255)
        .nullable()
        .describe(`A subcategory of the document's category. Null clears it. The subcategories of each category are: ${subcategoryCatalog}.`),
});
export const recategorizeAndReprocessSchema = z.object({
    documentId: displayId,
    primary: documentCategorySchema,
    secondary: z.string().max(255).nullable(),
});
export const documentOutputSchema = z.object({
    id: displayId,
    entity: entityOutputSchema,
    url: z.string(),
    filename: z.string(),
    type: z.string(),
    uploadedByUserId: z.number().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    summary: z.string().nullable(),
    summaryStatus: z.enum(['missing', 'pending', 'completed', 'failed']),
    summaryQueuedAt: z.date().nullable(),
    summaryClaimedAt: z.date().nullable(),
    summaryGeneratedAt: z.date().nullable(),
    categorization: documentCategorizationSchema,
});
export const documentFilterSchema = z.object({
    entityInput: entityInputSchema.optional(),
    exactMatch: z
        .boolean()
        .default(false)
        .describe('When true, return only rows filed against this entity. When false, include rows filed against anything inside it.'),
    category: documentCategorySchema.optional(),
    subcategory: z
        .string()
        .min(1)
        .max(255)
        .optional()
        .describe('A free-text label inside the category. Omit to match every subcategory.'),
});
export const getDocumentsSchema = documentFilterSchema.extend({
    limit: z.number().min(1).max(100).default(50).describe('How many rows to return.'),
    offset: z.number().min(0).default(0).describe('How many rows to skip, for paging.'),
});
export const getDocumentsOutputSchema = z.array(documentOutputSchema);
export const getDocumentsCountSchema = documentFilterSchema;
export const documentsCountOutputSchema = z.object({ total: z.number() });
export const getDocumentCategoryCountsSchema = z.object({
    entityInput: entityInputSchema.optional(),
    exactMatch: z
        .boolean()
        .default(false)
        .describe('When true, return only rows filed against this entity. When false, include rows filed against anything inside it.'),
});
export const documentCategoryCountsOutputSchema = z.object({
    counts: z.array(z.object({ category: z.string(), count: z.number() })),
    hasAssociationDocuments: z.boolean(),
});
export const documentWithDetailsOutputSchema = z.object({
    id: z.string(),
    filename: z.string(),
    type: z.string(),
    url: z.string(),
    entity: entityOutputSchema,
    uploadedByUserId: z.number().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    summary: z.string().nullable(),
    summaryStatus: z.enum(['missing', 'pending', 'completed', 'failed']),
    summaryQueuedAt: z.date().nullable(),
    summaryClaimedAt: z.date().nullable(),
    summaryGeneratedAt: z.date().nullable(),
    categorization: documentCategorizationSchema,
});
export const documentDetailOutputSchema = z.object({
    id: displayId,
    filename: z.string(),
    type: z.string(),
    url: z.string(),
    entity: entityOutputSchema,
    uploadedByUserId: z.number().nullable(),
    uploadedByName: z.string().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    summary: z.string().nullable(),
    summaryStatus: z.enum(['missing', 'pending', 'completed', 'failed']),
    summaryQueuedAt: z.date().nullable(),
    summaryClaimedAt: z.date().nullable(),
    summaryGeneratedAt: z.date().nullable(),
    categorization: documentCategorizationSchema,
});
export const uploadDocumentDataSchema = z.object({
    document: z.object({
        id: z.string(),
        filename: z.string(),
        type: z.string(),
        createdAt: z.date(),
    }),
});
export const uploadProposalDocumentDataSchema = uploadDocumentDataSchema.extend({
    vendorNameTask: z.object({
        displayId: z.string(),
    }),
});
export const deleteDocumentDataSchema = z.object({
    message: z.string(),
});
export const apiSuccessResponseSchema = z.object({
    success: z.literal(true),
});
export const apiErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
});
export const uploadDocumentSuccessSchema = apiSuccessResponseSchema.merge(uploadDocumentDataSchema);
export const uploadDocumentResponseSchema = z.discriminatedUnion('success', [
    uploadDocumentSuccessSchema,
    apiErrorResponseSchema,
]);
export const uploadProposalDocumentSuccessSchema = apiSuccessResponseSchema.merge(uploadProposalDocumentDataSchema);
export const uploadProposalDocumentResponseSchema = z.discriminatedUnion('success', [
    uploadProposalDocumentSuccessSchema,
    apiErrorResponseSchema,
]);
export const deleteDocumentSuccessSchema = apiSuccessResponseSchema.merge(deleteDocumentDataSchema);
export const deleteDocumentResponseSchema = z.discriminatedUnion('success', [
    deleteDocumentSuccessSchema,
    apiErrorResponseSchema,
]);
export const documentQASchema = z.object({
    documentId: displayId,
    documentType: z.enum(['file', 'document']).default('document'),
    question: z.string().min(1).max(2000),
    conversationHistory: z.array(llmChatMessageSchema).optional(),
});
export const documentQAOutputSchema = z.object({
    answer: z.string(),
});
export const getDocumentAccessTokenSchema = z.object({
    documentId: z.number(),
});
export const documentAccessTokenOutputSchema = z.object({
    accessToken: z.string(),
    expiresIn: z.number(),
    viewUrl: z.string(),
});
export const fileAttachmentSchema = z.object({
    attachmentId: displayId.describe('The attachment id that the question names, such as FILEA2B3C4D5.'),
    entityInput: entityInputSchema,
});
export const fileAttachmentOutputSchema = z.object({
    documentId: displayId,
    filename: z.string(),
});
//# sourceMappingURL=documents.js.map